import { useState, useEffect } from 'react'
import styled from 'styled-components'

import FileUploaderComponent from '../commons/components/FileUploaderComponent'
import LoadingComponent from '../commons/components/LoadingComponent'
import ModalComponent from '../commons/components/ModalComponent'
import RoundButtonComponent from '../commons/components/RoundButtonComponent'
import TextInputComponent from '../commons/components/TextInputComponent'
import { useUserContext } from '../context/UserContext'
import { INotification } from '../models/INotification'
import { ISource } from '../models/ISource'
import { getAllSources } from './API/SourceAPI'
import SourceOptionsListComponent from './SourceOptionsListComponent'
import { getFileNameByPath } from './utils/FileUtils'
import { MountedSource, mountSourceOptions } from './utils/SourceUtils'

export type NotificationCreationModalComponentProps = {
    isVisible: boolean
    selectedNotification: INotification | undefined
    setIsVisible: (isVisible: boolean) => void | null
    onSave: (
        item: INotification,
        files?: File[]
    ) => Promise<INotification | void> | void
    isSending: boolean
}

function NotificationCreationModalComponent({
    isVisible,
    selectedNotification,
    setIsVisible,
    onSave,
    isSending,
}: NotificationCreationModalComponentProps): JSX.Element {
    const { user } = useUserContext()
    const modalInitalValue: INotification = {
        name: '',
        description: '',
        sourceId: '',
        published: false,
        files: [],
        version: undefined,
    }

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [newNotification, setNewNotification] =
        useState<INotification>(modalInitalValue)
    const [sources, setSources] = useState<MountedSource[]>([])
    const [files, setFiles] = useState<File[]>([])

    const setDsmSource = (sources: ISource[]) => {
        setNewNotification((prev: INotification) => ({
            ...prev,
            sourceId: sources.find((source) => source.name === 'Gdv')!.id,
        }))
    }

    useEffect(() => {
        const fetchSources = async () => {
            const response: { data: ISource[] } = await getAllSources()
            if (response.data) {
                if (user.isDsm) {
                    setDsmSource(response.data)
                    setIsLoading(false)
                    return
                }
                const mountedSources: MountedSource[] = mountSourceOptions(
                    response.data.filter((source) => source.name !== 'Gdv')
                )
                setIsLoading(false)
                setSources(mountedSources)
            }
        }

        try {
            fetchSources()
        } catch (err) {
            console.error(err)
        }
    }, [])

    useEffect(() => {
        if (selectedNotification) {
            const notificationToEdit: INotification = {
                name: selectedNotification.name,
                sourceId: selectedNotification?.sourceId || '',
                description: selectedNotification?.description || '',
                id: selectedNotification?.id || '',
                published: false,
                files: selectedNotification?.files || [],
                version: selectedNotification?.version,
            }

            setNewNotification(notificationToEdit)
        }
    }, [selectedNotification])

    const handleChange = (value: string, name: string) => {
        setNewNotification((prev: INotification) => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleOnSave = async () => {
        setNewNotification(modalInitalValue)

        await onSave(newNotification, files)
        setIsVisible(false)
    }

    const handleOnClose = () => {
        setIsVisible(false)
        setNewNotification(modalInitalValue)
    }

    const getValue = (objectKey: string) => {
        return newNotification[objectKey as keyof INotification]
    }

    const isSaveDisabled = () =>
        Boolean(!newNotification.name || !newNotification.sourceId)

    const isSaveDisabledForGdv = () =>
        Boolean(!newNotification.name || !newNotification.description)

    const getFileNames = () => {
        return newNotification.files?.map((file) =>
            getFileNameByPath(file.path)
        )
    }

    if (isLoading) {
        return (
            <ModalComponent
                onClose={handleOnClose}
                isVisible={isVisible}
                title="Nova Notícia"
            >
                <LoadingComponentConatiner>
                    <LoadingComponent />
                </LoadingComponentConatiner>
            </ModalComponent>
        )
    }

    return (
        <ModalComponent
            onClose={handleOnClose}
            isVisible={isVisible}
            title="Nova Notícia"
        >
            <InputsContainer>
                {!user.isDsm && (
                    <BaseContainer>
                        <SourceOptionsListComponent
                            inputTitle="*LOCAL"
                            inputName="sourceId"
                            options={sources}
                            onChange={handleChange}
                            selectedValue={newNotification?.sourceId || ''}
                        />
                    </BaseContainer>
                )}
                <NameInputContainer>
                    <TextInputComponent
                        labelText="*NOME"
                        name="name"
                        onChange={handleChange}
                        getValue={getValue}
                        placeholder="Escreva o nome da nova notícia"
                    />
                </NameInputContainer>
                <BaseContainer>
                    <TextInputComponent
                        labelText="*DESCRIÇÃO"
                        name="description"
                        onChange={handleChange}
                        getValue={getValue}
                        isTextArea
                        maxLength={480}
                        helperText="Limite de 480 caracteres"
                        showCounter
                        placeholder="Descreva a notícia que será lançada"
                    />
                </BaseContainer>

                {!user.isDsm && (
                    <BaseContainer>
                        <TextInputComponent
                            labelText="NUMERO DA VERSÃO"
                            name="version"
                            onChange={handleChange}
                            getValue={getValue}
                            placeholder="Versão em lançamento"
                        />
                    </BaseContainer>
                )}

                <BaseContainer>
                    <FileUploaderComponent
                        maxFiles={10}
                        fileNames={
                            selectedNotification?.id
                                ? getFileNames()
                                : undefined
                        }
                        onFilesSelected={(files) => setFiles(files)}
                        onFileRemoved={(fileToRemove) => {
                            setFiles((prev) =>
                                prev.filter(
                                    (file) => file.name !== fileToRemove
                                )
                            )

                            setNewNotification((prev: INotification) => ({
                                ...prev,
                                files: prev.files?.filter(
                                    (file) =>
                                        getFileNameByPath(file.path) !==
                                        fileToRemove
                                ),
                            }))
                        }}
                        label="UPLOAD do ANEXO em pdf / IMAGEM (.JPG, .PNG)"
                        tooltip="Os arquivos irão aparecer na notícia em ordem alfabética. Além disso, arquivos .PDF virão listados sempre antes da exibição de imagens no carrossel."
                        accept={{
                            'image/*': ['.png', '.jpg'],
                            'application/pdf': ['.pdf'],
                        }}
                    />
                </BaseContainer>
            </InputsContainer>

            <SaveButtonContainer>
                <RoundButtonComponent
                    text="Salvar"
                    onClick={handleOnSave}
                    disabled={
                        user.isDsm ? isSaveDisabledForGdv() : isSaveDisabled()
                    }
                    isLoading={isSending}
                />
            </SaveButtonContainer>
        </ModalComponent>
    )
}

const LoadingComponentConatiner = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    padding-top: 230px;
    padding-bottom: 230px;
`

const InputsContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px 23px 35px 23px;
`

const NameInputContainer = styled.div`
    padding-top: 10px;
`
const BaseContainer = styled.div`
    margin-top: 23px;
`

const SaveButtonContainer = styled.div`
    display: flex;
    align-content: center;
    justify-content: center;
    padding-bottom: 20px;
`

export default NotificationCreationModalComponent
